import { t } from '@lingui/core/macro'
import { SentinelSummary } from 'features/sentinels/useSentinelList'
import { z } from 'zod'

export type TagCoverageStatus = 'active' | 'experimenting' | 'none'

export interface TagCoverage {
  status: TagCoverageStatus
  activeCount: number
  sentinelCount: number
  experimentCount: number
}

export const coverageFilterSchema = z.enum([
  'all',
  'active',
  'experimenting',
  'none',
])
export type CoverageFilter = z.infer<typeof coverageFilterSchema>

interface SavedExperimentLike {
  target?: string
}

export const deriveTagCoverage = (
  sentinels: readonly SentinelSummary[],
  savedExperiments: readonly SavedExperimentLike[]
): Map<string, TagCoverage> => {
  const byTag = new Map<string, TagCoverage>()
  const entryFor = (target: string): TagCoverage => {
    const existing = byTag.get(target)
    if (existing) return existing
    const fresh: TagCoverage = {
      status: 'none',
      activeCount: 0,
      sentinelCount: 0,
      experimentCount: 0,
    }
    byTag.set(target, fresh)
    return fresh
  }

  for (const sentinel of sentinels) {
    if (!sentinel.target) continue
    const entry = entryFor(sentinel.target)
    entry.sentinelCount += 1
    if (sentinel.monitorEnabled) entry.activeCount += 1
  }
  for (const experiment of savedExperiments) {
    if (!experiment.target) continue
    entryFor(experiment.target).experimentCount += 1
  }
  for (const entry of byTag.values()) {
    entry.status = entry.activeCount > 0 ? 'active' : 'experimenting'
  }
  return byTag
}

export const coverageStatusOf = (
  coverage: TagCoverage | undefined
): TagCoverageStatus => coverage?.status ?? 'none'

export const coverageTitle = (coverage: TagCoverage | undefined): string => {
  if (!coverage) return t`Unmonitored`
  const experiments = coverage.sentinelCount + coverage.experimentCount
  const experimentsLabel = `${experiments} experiment${experiments === 1 ? '' : 's'}`
  return coverage.status === 'active'
    ? t`Active monitor · ${experimentsLabel}`
    : experimentsLabel
}

export const matchesCoverageFilter = (
  filter: CoverageFilter,
  coverage: TagCoverage | undefined
): boolean => filter === 'all' || coverageStatusOf(coverage) === filter
